import React, { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNow } from "@/hooks/useNow";
import { formatDate } from "@/lib/formatDate";
import { useLocale } from "@/hooks/useLocale";
import { FeedPost } from "@/__generated__/types";
import { PhonePreview } from "@/components/PhoneFrame/PhonePreview";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";
import { Card, Separator } from "@/components/ui";
import { openExternalLink } from "@/util/externalLinkUtils";

interface PostDetailsContext {
  feedPost: FeedPost;
  loading: boolean;
}

export const PostDetails = () => {
  const { t } = useTranslation();
  const { feedPost } = useOutletContext<PostDetailsContext>();
  const now = useNow();
  const locale = useLocale();

  const postedByName = useMemo(
    () =>
      feedPost.postedBy?.name ||
      `${feedPost.postedBy?.firstName} ${feedPost.postedBy?.lastName || ""}` ||
      "",
    [feedPost.postedBy],
  );

  if (!feedPost) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-foreground">
          {t("feedPostDetails.details.notFound")}
        </div>
      </div>
    );
  }

  const publishDate = new Date(feedPost.publishDate * 1000);
  const isScheduled = publishDate > new Date(now);
  const hasCta = feedPost.ctaText;

  const formatPublishDate = () => {
    return formatDate(publishDate, "MMM d, yyyy, h:mm a", {
      locale: locale as "en",
    });
  };

  const organizationName = feedPost.organization?.name || "";

  return (
    <Card className="flex flex-col p-6 pb-0">
      <div className="flex items-start justify-between border-b py-6 pb-10">
        <div className="flex flex-grow flex-col gap-8">
          <div className="grid grid-cols-3 gap-8">
            <InputWrapper label={t("feedPostDetails.postDetails.postedBy")}>
              <p className="text-sm text-muted-foreground">{postedByName}</p>
            </InputWrapper>

            <InputWrapper
              label={t("feedPostDetails.postDetails.ctaButtonText")}
            >
              <p className="text-sm text-muted-foreground">
                {feedPost.ctaText || "-"}
              </p>
            </InputWrapper>

            <InputWrapper label={t("feedPostDetails.postDetails.ctaButtonUrl")}>
              <p className="text-sm text-muted-foreground">
                {feedPost.ctaUrl ? (
                  <a
                    href={feedPost.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {feedPost.ctaUrl}
                  </a>
                ) : (
                  "-"
                )}
              </p>
            </InputWrapper>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <InputWrapper label={t("feedPostDetails.postDetails.caption")}>
              <p className="text-sm text-muted-foreground">
                {feedPost.caption || t("feedPostDetails.postDetails.noCaption")}
              </p>
            </InputWrapper>

            <InputWrapper
              label={
                isScheduled
                  ? t("feedPostDetails.postDetails.scheduledForLater")
                  : t("feedPostDetails.postDetails.postedAt")
              }
            >
              <p className="text-sm text-muted-foreground">
                {formatPublishDate()}
              </p>
            </InputWrapper>
          </div>
        </div>
      </div>

      <div className="pt-10">
        <div className="flex justify-center overflow-hidden">
          <PhonePreview
            className="mb-[-32px] w-full max-w-[400px]"
            showStatusBar={true}
            showHomeIndicator={true}
          >
            <div className="space-y-4 p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={feedPost.postedBy?.profilePhoto?.url || undefined}
                    alt={postedByName}
                  />
                  <AvatarFallback>{postedByName.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {postedByName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {organizationName}
                  </p>
                </div>
              </div>

              {feedPost.image && (
                <div className="w-full">
                  <img
                    src={feedPost.image.url || ""}
                    alt={t("feedPostDetails.postDetails.feedPostImage")}
                    className="w-full rounded-lg object-cover"
                    style={{
                      aspectRatio:
                        feedPost.image.width === feedPost.image.height
                          ? "1 / 1"
                          : "3 / 4",
                    }}
                  />
                </div>
              )}

              {feedPost.video && (
                <div className="w-full">
                  <video
                    src={feedPost.video.url || ""}
                    className="w-full rounded-lg object-cover"
                    style={{
                      aspectRatio:
                        feedPost.video.width === feedPost.video.height
                          ? "1 / 1"
                          : "3 / 4",
                    }}
                    controls
                    playsInline
                  />
                </div>
              )}

              {hasCta && (
                <div className="w-full">
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() =>
                      feedPost.ctaUrl && openExternalLink(feedPost.ctaUrl)
                    }
                    disabled={!feedPost.ctaUrl}
                  >
                    {feedPost.ctaText}
                  </Button>
                </div>
              )}

              <Separator className="mt-4 mb-3" />

              {feedPost.caption && (
                <div className="w-full">
                  <p className="text-sm whitespace-pre-wrap text-foreground">
                    {feedPost.caption}
                  </p>
                </div>
              )}
            </div>
          </PhonePreview>
        </div>
      </div>
    </Card>
  );
};
